// AlsokPage — ALSOK 入金処理ページ (2026-05-07)
//
// ARICA `/api/alsok/inputs` から取得した closing_date 単位の集計を 給与ページ風 仕訳プレビュー で表示。
// 月選択 → 各 closing_date ごとに deal A / deal B / wallet_txn match / audit warnings を表示。
// 承認ボタン 1 click で worker /api/alsok/register-day → freee deal 作成 + wallet_txn 消込まで完結。
//
// 重複防止 (4 層):
//   (a) alsok_runs.closing_date PK
//   (b) freee ref_number = alsok:{closing_date}:a / :b
//   (c) invoice_registrations.idempotency_key UNIQUE
//   (d) freee /api/1/deals 直接 search (D1/freee 状態乖離時の保険)

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container,
  PageTitle,
  CardBase,
  HStack,
  VStack,
  Button,
  Note,
  StatusIcon,
  InlineSpinner,
  MessageDialog,
  MessageDialogConfirm,
} from '@freee_jp/vibes';
import {
  MdSecurity,
  MdRefresh,
  MdCheckCircle,
  MdWarning,
  MdError,
  MdAccountBalance,
  MdCalendarToday,
  MdLink,
  MdAttachMoney,
  MdLocalAtm,
  MdAssignmentTurnedIn,
  MdSwapHoriz,
} from 'react-icons/md';
import {
  fetchAlsokPreviewMonth,
  registerAlsokDay,
  openClaudeSessionForAlsok,
  fetchAlsokTransferPreview,
  registerAlsokTransfer,
  AlsokMonthPreview,
  AlsokDayPreview,
  AlsokDealPayload,
  AlsokDealDetail,
  AlsokTransferPreview,
  AlsokTransferDay,
  AlsokTransferStatus,
} from '../api/client';

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
const yen = (n: number | null | undefined) =>
  n == null ? '—' : `¥${Number(n).toLocaleString('ja-JP')}`;

const DAY_LABEL = ['日', '月', '火', '水', '木', '金', '土'];
const dayOfWeek = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00Z');
  return DAY_LABEL[d.getUTCDay()];
};

const currentMonthYm = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const prevMonthYm = () => {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

// ---------------------------------------------------------------------------
// Styles (PayrollPage 流用)
// ---------------------------------------------------------------------------
const S = {
  page: {
    background: 'linear-gradient(180deg, #f8f7f5 0%, #f3f1ef 100%)',
    minHeight: '100vh', paddingBottom: '3rem',
  } as React.CSSProperties,
  controlBar: {
    display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' as const,
    padding: '12px 16px', background: '#fff',
    border: '1px solid #e5e4e2', borderRadius: '10px', marginBottom: '16px',
  } as React.CSSProperties,
  kpiRow: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '10px', marginBottom: '16px',
  } as React.CSSProperties,
  kpiCard: (accent: string): React.CSSProperties => ({
    background: `linear-gradient(135deg, ${accent}12 0%, ${accent}05 100%)`,
    border: `1px solid ${accent}22`,
    borderRadius: '12px',
    padding: '12px 16px',
  }),
  kpiLabel: { fontSize: '0.6875rem', color: '#6b6560', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' as const },
  kpiValue: { fontSize: '1.125rem', fontWeight: 700, marginTop: '4px' } as React.CSSProperties,
  kpiSub: { fontSize: '0.75rem', color: '#8a837c', marginTop: '2px' } as React.CSSProperties,

  dayCard: (status: string): React.CSSProperties => {
    const colorMap: Record<string, { bd: string; lf: string }> = {
      registered: { bd: '#c6e7d3', lf: '#2da663' },
      partial:    { bd: '#ffd9a8', lf: '#d68a3c' },
      proposed:   { bd: '#cfdcef', lf: '#48859d' },
      approved:   { bd: '#c5d1f0', lf: '#2e7cf6' },
      failed:     { bd: '#f0c4c4', lf: '#c94444' },
      pending:    { bd: '#e5e4e2', lf: '#a89c92' },
    };
    const c = colorMap[status] || colorMap.pending;
    return {
      background: '#fff',
      border: `1px solid ${c.bd}`,
      borderLeft: `5px solid ${c.lf}`,
      borderRadius: '10px',
      padding: '16px 20px',
      marginBottom: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    };
  },
  dealHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    gap: '12px', marginBottom: '10px',
  } as React.CSSProperties,
  dayBadge: (color: string): React.CSSProperties => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: '6px',
    background: `${color}22`, color, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em',
  }),
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.8125rem', marginTop: '6px' } as React.CSSProperties,
  th: {
    padding: '7px 10px', fontSize: '0.6875rem', fontWeight: 700, color: '#6b6560',
    textTransform: 'uppercase' as const, letterSpacing: '0.04em',
    borderBottom: '1px solid #e5e4e2', background: '#fafaf8',
    textAlign: 'left' as const, whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,
  td: {
    padding: '6px 10px', fontSize: '0.8125rem',
    borderBottom: '1px solid #f4f3f1', verticalAlign: 'top' as const,
  } as React.CSSProperties,
  amt: { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const, whiteSpace: 'nowrap' as const },
  sectionCaption: { fontSize: '0.6875rem', color: '#8a837c' },
  warningBox: {
    marginTop: '10px', padding: '8px 12px',
    background: '#fff4e2', border: '1px solid #f5d8a8', borderRadius: '6px',
    color: '#8b5a14', fontSize: '0.8125rem',
  } as React.CSSProperties,
  matchBox: {
    marginTop: '10px', padding: '8px 12px',
    background: '#f1f6fa', border: '1px solid #d5e2ec', borderRadius: '6px',
    fontSize: '0.8125rem',
  } as React.CSSProperties,
  freeeLink: {
    color: '#2e7cf6', textDecoration: 'none', fontWeight: 700,
  } as React.CSSProperties,
};

const FREEE_DEAL_BASE = 'https://secure.freee.co.jp/deals?company_id=11518223&deal_id=';

// freee 上の正式 walletable_name (= 表示用、 省略禁止)
const WALLET_LABELS: Record<string, Record<number, string>> = {
  bank_account: {
    3838033: '三井住友銀行 普通預金',
    3838100: '西武信用金庫 現金管理',
    3838203: '三菱東京UFJ銀行 喜乃字屋口座',
    3838050: '三井住友銀行 喜乃字屋口座',
  },
  wallet: {
    6559057: '決済口座',
    6361178: '本部両替金 (= 店舗釣銭・小口現金用 ALSOK経由 本部金庫入金)',
    6361308: '本部入金高',
  },
};
const walletLabel = (type?: string, id?: number | null) => {
  if (!type || !id) return '—';
  return WALLET_LABELS[type]?.[id] || `${type === 'wallet' ? 'wallet' : 'bank'} ${id}`;
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const DealTable: React.FC<{ label: string; rows: AlsokDealDetail[] }> = ({ label, rows }) => {
  if (!rows.length) return null;
  const total = rows.reduce((s, r) => s + r.amount, 0);
  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b6560', marginBottom: '4px' }}>
        {label} <span style={{ color: '#8a837c', fontWeight: 400 }}>({rows.length}行 / 計 {yen(total)})</span>
      </div>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>科目</th>
            <th style={S.th}>部門</th>
            <th style={S.th}>摘要</th>
            <th style={{ ...S.th, ...S.amt }}>金額</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={S.td}>
                {r.accountName}
                <div style={S.sectionCaption}>AI {r.account_item_id} · tax {r.taxCodeName}</div>
              </td>
              <td style={S.td}>
                {r.sectionName || '—'}
                {r.section_id && <div style={S.sectionCaption}>sec {r.section_id}</div>}
              </td>
              <td style={S.td}>{r.description}</td>
              <td style={{ ...S.td, ...S.amt, fontWeight: 500 }}>{yen(r.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DealCard: React.FC<{
  title: string;
  subtitle: string;
  accent: string;
  payload: AlsokDealPayload;
  freeeDealId?: number | null;
  hint?: string;
  creditLabel?: string;
  debitLabel?: string;
}> = ({ title, subtitle, accent, payload, freeeDealId, hint, creditLabel, debitLabel }) => {
  const debitSum = payload.debit_details.reduce((s, d) => s + d.amount, 0);
  const creditSum = payload.credit_details.reduce((s, d) => s + d.amount, 0);
  const balanceOk = (creditSum - debitSum) === payload.amount;
  return (
    <div style={{
      background: '#fff', border: `1px solid ${accent}22`, borderRadius: '8px',
      padding: '12px 14px', marginTop: '8px',
    }}>
      <div style={S.dealHeader}>
        <div style={{ flex: '1 1 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' as const }}>
            <span style={S.dayBadge(accent)}>{title}</span>
            <span style={{ fontWeight: 700, fontSize: '0.875rem' }}>{subtitle}</span>
            {balanceOk
              ? <StatusIcon type="done">balance OK</StatusIcon>
              : <StatusIcon type="error">差額 {yen(creditSum - debitSum - payload.amount)}</StatusIcon>}
            {freeeDealId && (
              <a href={`${FREEE_DEAL_BASE}${freeeDealId}`} target="_blank" rel="noopener noreferrer" style={S.freeeLink}>
                freee #{freeeDealId} <MdLink size={12} />
              </a>
            )}
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '6px', fontSize: '0.75rem', color: '#6b6560', flexWrap: 'wrap' as const }}>
            <span>発生日: <b style={{ color: '#393634' }}>{payload.issue_date}</b></span>
            <span>期日: <b style={{ color: '#393634' }}>{payload.due_date}</b></span>
            {payload.payments.length > 0 && (
              <span>
                決済: <b style={{ color: '#393634' }}>{walletLabel(payload.payments[0].from_walletable_type, payload.payments[0].from_walletable_id)}</b>
              </span>
            )}
            <span>ref: <code style={{ fontSize: '0.6875rem', color: '#8a837c' }}>{payload.ref_number}</code></span>
          </div>
          {hint && (
            <div style={{ fontSize: '0.75rem', color: '#6b6560', marginTop: '4px', padding: '4px 8px', background: '#fff7e8', border: '1px solid #f3dcae', borderRadius: '4px' }}>
              {hint}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right' as const, flexShrink: 0 }}>
          <div style={{ fontSize: '1rem', fontWeight: 700 }}>{yen(payload.amount)}</div>
          <Note>借 {yen(debitSum)} / 貸 {yen(creditSum)}</Note>
        </div>
      </div>
      <div style={{ fontSize: '0.75rem', padding: '4px 8px', background: '#fafaf8', borderRadius: '4px', color: '#555', fontStyle: 'italic' as const }}>
        {payload.memo}
      </div>
      <DealTable label={creditLabel || '貸方 (Credit)'} rows={payload.credit_details} />
      <DealTable label={debitLabel || '借方 (Debit)'} rows={payload.debit_details} />
    </div>
  );
};

const DayCard: React.FC<{
  day: AlsokDayPreview;
  onApprove: (closingDate: string) => Promise<void>;
  registering: boolean;
}> = ({ day, onApprove, registering }) => {
  const status = day.registration_status?.run?.status || 'pending';
  const isRegistered = status === 'registered' || status === 'registered_backfill';
  const isPartial = status === 'partial';
  const isFailed = status === 'failed';
  const dow = dayOfWeek(day.closing_date);
  const dowLabel = day.is_monday ? '月曜入金 (= 金曜分 ¥325k 持出 反映)'
    : day.is_tuesday ? '火曜入金 (= 月曜分 ¥420k 持出 反映)'
    : day.is_friday ? '金曜入金 (= 水〜金 3日分 集約)'
    : '通常入金';

  return (
    <div style={S.dayCard(status)}>
      {/* Header */}
      <div style={S.dealHeader}>
        <div style={{ flex: '1 1 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' as const }}>
            <span style={{ fontSize: '1.125rem', fontWeight: 700 }}>
              <MdCalendarToday size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              {day.closing_date} <span style={{ color: '#8a837c' }}>({dow})</span>
            </span>
            {isRegistered && <StatusIcon type="done">登録完了</StatusIcon>}
            {isPartial && <StatusIcon type="warning">部分登録</StatusIcon>}
            {isFailed && <StatusIcon type="warning">対応待ち(処理失敗)</StatusIcon>}
            {!isRegistered && !isPartial && !isFailed && status === 'pending' && <StatusIcon type="required">未処理</StatusIcon>}
            {(day.is_monday || day.is_tuesday || day.is_friday) && (
              <span style={S.dayBadge('#48859d')}>{dowLabel}</span>
            )}
            {day.unknown_regi_codes.length > 0 && (
              <span style={S.dayBadge('#c94444')}>未知 regi: {day.unknown_regi_codes.join(',')}</span>
            )}
          </div>
          <Note>
            ARICA {day.record_count} 件 / {day.section_count} 拠点 ·
            {day.consolidated_days > 1
              ? <> <b style={{ color: '#d68a3c' }}>連休 {day.consolidated_days} 日 集約</b> ({day.arica_closing_dates.join(', ')})</>
              : <> 単日 banking</>}
          </Note>
        </div>
        <div style={{ textAlign: 'right' as const, flexShrink: 0 }}>
          <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>
            {yen(day.bank_actual_amount ?? day.expected_a_amount)}
          </div>
          <Note>銀行入金額 (実績)</Note>
        </div>
      </div>

      {/* wallet_txn match */}
      <div style={S.matchBox}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#48859d', marginBottom: '4px' }}>
          <MdAccountBalance size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          銀行入出金との突合 — 対象口座: <b>三井住友銀行 普通預金</b>
        </div>
        {day.wallet_txn ? (
          <Note>
            wallet_txn #{day.wallet_txn.wallet_txn_id} · {yen(day.wallet_txn.amount)} · status: {day.wallet_txn.status_raw === 2 ? '消込済' : '未消込'}
            {day.amount_diff != null && day.amount_diff !== 0 && (
              <> · <b style={{ color: '#c94444' }}>差額 {yen(day.amount_diff)}</b></>
            )}
            {day.amount_diff === 0 && <> · <b style={{ color: '#2da663' }}>balance OK</b></>}
          </Note>
        ) : (
          <Note><b style={{ color: '#c94444' }}>該当 wallet_txn 未取得</b> (= 銀行 sync 待ち or 入金未到着)</Note>
        )}
      </div>

      {/* audit warnings */}
      {day.audit_warnings.length > 0 && (
        <div style={S.warningBox}>
          <div style={{ fontWeight: 700, marginBottom: '4px' }}>
            <MdWarning size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            警告
          </div>
          {day.audit_warnings.map((w, i) => (
            <div key={i} style={{ marginLeft: '20px' }}>• {w}</div>
          ))}
        </div>
      )}

      {/* Deal A preview */}
      <DealCard
        title="取引A"
        subtitle="メイン ALSOK 入金 → 三井住友銀行 普通預金"
        accent="#2da663"
        payload={day.deal_a}
        freeeDealId={day.registration_status?.run?.freee_deal_a_id ?? null}
        hint="ALSOK が当該 closing_date の各店舗投入現金を集計し、 本部両替金 ALSOK持参分 (= 月¥325k/火¥420k) を負の credit で差引いた残額を 三井住友銀行 普通預金 に入金。 wallet_txn と消込される。 持出 line は 過去パターン互換で entry_side='credit' amount=NEGATIVE。"
        creditLabel="貸方 (Credit) — 各拠点売上現金 + 本部両替金/本部入金高 一旦受取 (= 入金機投入高)"
        debitLabel="借方相当 (= 持出 マイナス credit) — 本部両替金 ALSOK持参分 控除"
      />

      {/* Deal B preview (if mon/tue/holiday-followup) */}
      {day.deal_b && (
        <DealCard
          title="取引B"
          subtitle="ALSOK持出 → 本部両替金 wallet INFLOW (+)"
          accent="#48859d"
          payload={day.deal_b}
          freeeDealId={day.registration_status?.run?.freee_deal_b_id ?? null}
          hint={
            'ALSOK が 銀行ではなく 本部金庫に持参した現金 (= 月¥325k/火¥420k/連休明け¥745k) を 本部両替金 wallet (= 6361178、 店舗釣銭・小口現金用) への INFLOW として記録。 '
            + 'freee POST 時に wallet 6361178 の wallet_txn が自動生成 (= 別途 reconcile 不要)。'
          }
          creditLabel="貸方 (Credit) — 本部両替金 wallet INFLOW"
          debitLabel="借方"
        />
      )}

      {/* Deal C preview (regi 100 wallet 6361178 OUTFLOW) */}
      {day.deal_c && (
        <DealCard
          title="取引C"
          subtitle="本部両替金 設定額超過分 → ALSOK 経由 銀行入金 (= wallet OUTFLOW −)"
          accent="#d68a3c"
          payload={day.deal_c}
          freeeDealId={day.registration_status?.run?.freee_deal_c_id ?? null}
          hint={
            '本部両替金 wallet (= 6361178) は 設定額 (= 釣銭用紙幣の固定額) を保持。 超過分 (= regi 100、 ARICA records) は ALSOK 経由で銀行入金される。 '
            + '取引A で 入金機投入高 sec=本部 として一旦 credit したものを、 本仕訳で 入金機投入高 を debit して打ち消し + 本部両替金 wallet を OUTFLOW (= 出金) として 設定額残高を補正。 '
            + 'これが無いと wallet 6361178 残高が 帳簿と物理現金で一致しなくなる。'
          }
          creditLabel="貸方 (Credit)"
          debitLabel="借方 (Debit) — 入金機投入高 sec=本部 (= 取引A credit を打ち消し)"
        />
      )}

      {/* Deal D preview (regi 101 wallet 6361308 OUTFLOW) */}
      {day.deal_d && (
        <DealCard
          title="取引D"
          subtitle="本部入金高 → ALSOK 経由 銀行入金 (= wallet OUTFLOW −)"
          accent="#8a6cd6"
          payload={day.deal_d}
          freeeDealId={day.registration_status?.run?.freee_deal_d_id ?? null}
          hint={
            '本部入金高 wallet (= 6361308) は 本部現金売上 (= 雑収入、 入金伝票/赤伝票で計上済) の保持口座。 '
            + 'その現金が ALSOK 経由で銀行入金される (= regi 101、 ARICA records)。 '
            + '取引A で 入金機投入高 sec=本部 として一旦 credit したものを、 本仕訳で 入金機投入高 を debit して打ち消し + 本部入金高 wallet OUTFLOW を計上 (= 既存 雑収入 deal の消込が成立)。'
          }
          creditLabel="貸方 (Credit)"
          debitLabel="借方 (Debit) — 入金機投入高 sec=本部 (= 取引A credit を打ち消し)"
        />
      )}

      {/* Approve button */}
      <div style={{ marginTop: '12px', textAlign: 'right' as const }}>
        {isRegistered ? (
          <Note><MdCheckCircle size={14} style={{ verticalAlign: 'middle', color: '#2da663', marginRight: 4 }} />
            登録完了 ({day.registration_status?.run?.registered_at?.slice(0, 16)})
          </Note>
        ) : (
          <Button
            primary
            disabled={
              registering
              || day.unknown_regi_codes.length > 0
              || !day.wallet_txn
              || day.wallet_txn?.status_raw === 2
              || (day.amount_diff !== null && day.amount_diff !== 0)
            }
            onClick={() => onApprove(day.closing_date)}
          >
            {registering ? <InlineSpinner isLoading /> : <MdAssignmentTurnedIn size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
            {isPartial ? '再試行' : '承認 → freee 登録 + 消込'}
          </Button>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 振替バックフィル (取引B/C/D — 入金機投入高 → wallet 振替) sub-components
// ---------------------------------------------------------------------------
const TRANSFER_STATUS_META: Record<AlsokTransferStatus, { label: string; accent: string }> = {
  needs_transfer:    { label: '振替未登録', accent: '#d68a3c' },
  fully_registered:  { label: '振替完了',   accent: '#2da663' },
  partial:           { label: '部分登録',   accent: '#caa50a' },
  blocked:           { label: 'ブロック',   accent: '#c94444' },
};

const TransferDayCard: React.FC<{
  day: AlsokTransferDay;
  onApprove: (closingDate: string) => Promise<void>;
  registering: boolean;
}> = ({ day, onApprove, registering }) => {
  const meta = TRANSFER_STATUS_META[day.transfer_status] || TRANSFER_STATUS_META.blocked;
  const dow = dayOfWeek(day.closing_date);
  const sr = day.source_rows;
  const balanceAfterOk = day.balance_check?.clearing_residual_after === 0;
  const allExisting = day.existing.b && day.existing.c && day.existing.d;
  const canApprove =
    day.has_deal_a
    && day.transfer_status !== 'fully_registered'
    && day.transfer_status !== 'blocked'
    && !allExisting
    && !registering;

  return (
    <div style={S.dayCard(
      day.transfer_status === 'fully_registered' ? 'registered'
      : day.transfer_status === 'partial' ? 'partial'
      : day.transfer_status === 'blocked' ? 'failed'
      : 'proposed',
    )}>
      {/* Header */}
      <div style={S.dealHeader}>
        <div style={{ flex: '1 1 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' as const }}>
            <span style={{ fontSize: '1.0625rem', fontWeight: 700 }}>
              <MdCalendarToday size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              {day.closing_date} <span style={{ color: '#8a837c' }}>({dow})</span>
            </span>
            <span style={S.dayBadge(meta.accent)}>{meta.label}</span>
            {day.has_deal_a && day.deal_a_id && (
              <a href={`${FREEE_DEAL_BASE}${day.deal_a_id}`} target="_blank" rel="noopener noreferrer" style={S.freeeLink}>
                取引A freee #{day.deal_a_id} <MdLink size={12} />
              </a>
            )}
            {!day.has_deal_a && (
              <StatusIcon type="error">取引A 未登録 (= 振替候補外)</StatusIcon>
            )}
            <span style={S.dayBadge(day.existing.b ? '#2da663' : '#a89c92')}>B {day.existing.b ? '済' : '未'}</span>
            <span style={S.dayBadge(day.existing.c ? '#2da663' : '#a89c92')}>C {day.existing.c ? '済' : '未'}</span>
            <span style={S.dayBadge(day.existing.d ? '#2da663' : '#a89c92')}>D {day.existing.d ? '済' : '未'}</span>
          </div>
          <Note>
            <MdSwapHoriz size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            取引A から抽出した相殺対象額 (= recompute なし、 取引A 実明細の額をそのまま使用)
          </Note>
        </div>
        <div style={{ textAlign: 'right' as const, flexShrink: 0 }}>
          <div style={{ fontSize: '0.75rem', color: '#6b6560' }}>入金機投入高 本部 滞留</div>
          <div style={{ fontSize: '1rem', fontWeight: 700 }}>{yen(day.balance_check?.clearing_residual_before)}</div>
          {balanceAfterOk
            ? <StatusIcon type="done">相殺後 0</StatusIcon>
            : <StatusIcon type="warning">相殺後 {yen(day.balance_check?.clearing_residual_after)}</StatusIcon>}
        </div>
      </div>

      {/* source_rows readout */}
      <div style={S.matchBox}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#48859d', marginBottom: '4px' }}>
          <MdAttachMoney size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          相殺対象 (= 取引A 本部行から抽出)
        </div>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' as const, fontSize: '0.8125rem' }}>
          <span>両替金持参 (→ 取引B): <b>{yen(sr.exchange_carry)}</b></span>
          <span>regi100 本部両替金cr (→ 取引C): <b>{yen(sr.regi100)}</b></span>
          <span>regi101 本部入金高cr (→ 取引D): <b>{yen(sr.regi101)}</b></span>
        </div>
      </div>

      {/* audit warnings */}
      {day.audit_warnings.length > 0 && (
        <div style={S.warningBox}>
          <div style={{ fontWeight: 700, marginBottom: '4px' }}>
            <MdWarning size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            警告
          </div>
          {day.audit_warnings.map((w, i) => (
            <div key={i} style={{ marginLeft: '20px' }}>• {w}</div>
          ))}
        </div>
      )}

      {/* Deal B / C / D previews (reuse DealCard) */}
      {day.deal_b && (
        <DealCard
          title="取引B"
          subtitle="本部両替金 wallet 補充 (= 両替金持参 offset)"
          accent="#48859d"
          payload={day.deal_b}
          freeeDealId={null}
          hint={
            '取引A の 両替金持参 (= 負 credit) で 入金機投入高 に借方計上された分を、 '
            + 'income / Cr 入金機投入高 + payment from wallet 6361178 (本部両替金) で相殺。 '
            + 'freee POST 時に wallet 6361178 の wallet_txn が自動生成 (= 別途 reconcile 不要)。'
          }
          creditLabel="貸方 (Credit) — 入金機投入高 (= 取引A 両替金持参 借方を相殺)"
          debitLabel="借方"
        />
      )}
      {day.deal_c && (
        <DealCard
          title="取引C"
          subtitle="本部両替金 wallet 戻し (= regi100 offset)"
          accent="#d68a3c"
          payload={day.deal_c}
          freeeDealId={null}
          hint={
            '取引A の regi100 (= 本部両替金cr) で 入金機投入高 に貸方計上された分を、 '
            + 'expense / Dr 入金機投入高 + payment from wallet 6361178 (本部両替金) で相殺。'
          }
          creditLabel="貸方 (Credit)"
          debitLabel="借方 (Debit) — 入金機投入高 (= 取引A regi100 貸方を相殺)"
        />
      )}
      {day.deal_d && (
        <DealCard
          title="取引D"
          subtitle="本部入金高 wallet (= regi101 offset)"
          accent="#8a6cd6"
          payload={day.deal_d}
          freeeDealId={null}
          hint={
            '取引A の regi101 (= 本部入金高cr) で 入金機投入高 に貸方計上された分を、 '
            + 'expense / Dr 入金機投入高 + payment from wallet 6361308 (本部入金高) で相殺 (= 既存 雑収入 deal の消込が成立)。'
          }
          creditLabel="貸方 (Credit)"
          debitLabel="借方 (Debit) — 入金機投入高 (= 取引A regi101 貸方を相殺)"
        />
      )}

      {/* Approve button */}
      <div style={{ marginTop: '12px', textAlign: 'right' as const }}>
        {day.transfer_status === 'fully_registered' || allExisting ? (
          <Note><MdCheckCircle size={14} style={{ verticalAlign: 'middle', color: '#2da663', marginRight: 4 }} />
            振替 (取引B/C/D) 登録済み
          </Note>
        ) : !day.has_deal_a ? (
          <Note><MdError size={14} style={{ verticalAlign: 'middle', color: '#c94444', marginRight: 4 }} />
            取引A 未登録のため振替不可 (= 取引A を先に登録)
          </Note>
        ) : day.transfer_status === 'blocked' ? (
          <Note><MdError size={14} style={{ verticalAlign: 'middle', color: '#c94444', marginRight: 4 }} />
            ブロック中 (= 警告を確認)
          </Note>
        ) : (
          <Button
            primary
            disabled={!canApprove}
            onClick={() => onApprove(day.closing_date)}
          >
            {registering ? <InlineSpinner isLoading /> : <MdSwapHoriz size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
            {day.transfer_status === 'partial' ? '残りを登録 → 振替B/C/D' : '承認 → 振替B/C/D 登録'}
          </Button>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export const AlsokPage: React.FC = () => {
  const [targetMonth, setTargetMonth] = useState<string>(prevMonthYm());
  const [preview, setPreview] = useState<AlsokMonthPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registeringDate, setRegisteringDate] = useState<string | null>(null);
  const [confirmDate, setConfirmDate] = useState<string | null>(null);
  const [resultMsg, setResultMsg] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // 振替バックフィル (取引B/C/D) state
  const [transferPreview, setTransferPreview] = useState<AlsokTransferPreview | null>(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [transferRegisteringDate, setTransferRegisteringDate] = useState<string | null>(null);
  const [transferConfirmDate, setTransferConfirmDate] = useState<string | null>(null);

  // Codex fix #8: 直接 return value 受け取って closure stale 回避
  const loadSession = useCallback(async (): Promise<string | null> => {
    const sid = await openClaudeSessionForAlsok();
    if (sid) setSessionId(sid);
    return sid;
  }, []);

  const loadPreview = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await fetchAlsokPreviewMonth(targetMonth);
      setPreview(data);
    } catch (e: any) {
      setError(e.message || 'プレビュー取得失敗');
    } finally {
      setLoading(false);
    }
  }, [targetMonth]);

  const loadTransferPreview = useCallback(async () => {
    setTransferLoading(true); setTransferError(null);
    try {
      const data = await fetchAlsokTransferPreview(targetMonth);
      setTransferPreview(data);
    } catch (e: any) {
      setTransferError(e.message || '振替プレビュー取得失敗');
    } finally {
      setTransferLoading(false);
    }
  }, [targetMonth]);

  useEffect(() => { loadSession(); }, [loadSession]);
  useEffect(() => { loadPreview(); }, [loadPreview]);
  useEffect(() => { loadTransferPreview(); }, [loadTransferPreview]);

  const handleApprove = useCallback(async (closingDate: string) => {
    if (!sessionId) {
      const fresh = await loadSession();
      if (!fresh) {
        setError('session 取得失敗');
        return;
      }
    }
    setConfirmDate(closingDate);
  }, [sessionId, loadSession]);

  const performRegister = useCallback(async (closingDate: string) => {
    setConfirmDate(null);
    let sid = sessionId;
    if (!sid) {
      sid = await loadSession();
      if (!sid) { setResultMsg(`${closingDate}: session 取得失敗`); return; }
    }
    setRegisteringDate(closingDate);
    try {
      const result = await registerAlsokDay(closingDate, sid);
      setResultMsg(`${closingDate}: ${result.status}${result.results?.deal_a && 'freee_deal_id' in result.results.deal_a
        ? ` (deal_a #${result.results.deal_a.freee_deal_id})` : ''}`);
      // refresh preview
      await loadPreview();
    } catch (e: any) {
      setResultMsg(`${closingDate}: 失敗 ${e.message}`);
    } finally {
      setRegisteringDate(null);
    }
  }, [sessionId, loadPreview, loadSession]);

  // 振替バックフィル approve flow (R2: UI click = freee POST 起点)
  const handleTransferApprove = useCallback(async (closingDate: string) => {
    if (!sessionId) {
      const fresh = await loadSession();
      if (!fresh) { setTransferError('session 取得失敗'); return; }
    }
    setTransferConfirmDate(closingDate);
  }, [sessionId, loadSession]);

  const performTransferRegister = useCallback(async (closingDate: string) => {
    setTransferConfirmDate(null);
    let sid = sessionId;
    if (!sid) {
      sid = await loadSession();
      if (!sid) { setResultMsg(`${closingDate}: session 取得失敗`); return; }
    }
    setTransferRegisteringDate(closingDate);
    try {
      const result = await registerAlsokTransfer(closingDate, sid);
      const parts: string[] = [];
      const tagMap: Record<'deal_b' | 'deal_c' | 'deal_d', string> = {
        deal_b: '取引B', deal_c: '取引C', deal_d: '取引D',
      };
      for (const k of ['deal_b', 'deal_c', 'deal_d'] as const) {
        const r = result.results?.[k];
        if (!r) continue;
        const tag = tagMap[k];
        if ('freee_deal_id' in r) parts.push(`${tag} #${r.freee_deal_id}${r.was_phantom ? '(既存)' : ''}`);
        else if ('skipped' in r) parts.push(`${tag} skip`);
        else if ('error' in r) parts.push(`${tag} err:${r.error}`);
      }
      setResultMsg(`${closingDate} 振替: ${result.status}${parts.length ? ` (${parts.join(' / ')})` : ''}`);
      await loadTransferPreview();
    } catch (e: any) {
      setResultMsg(`${closingDate} 振替: 失敗 ${e.message}`);
    } finally {
      setTransferRegisteringDate(null);
    }
  }, [sessionId, loadTransferPreview, loadSession]);

  const summary = preview?.summary;
  const transferSummary = transferPreview?.summary;

  return (
    <div style={S.page}>
      <Container>
        <PageTitle>
          <HStack>
            <MdSecurity size={28} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            ALSOK 入金処理
          </HStack>
        </PageTitle>

        {/* Control bar */}
        <div style={S.controlBar}>
          <span style={{ fontWeight: 700 }}>対象月:</span>
          <input
            type="month"
            value={targetMonth}
            onChange={(e) => setTargetMonth(e.target.value)}
            style={{
              padding: '6px 10px', fontSize: '0.875rem',
              border: '1px solid #d4d2cf', borderRadius: '6px',
            }}
          />
          <Button onClick={loadPreview} disabled={loading}>
            <MdRefresh size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            最新の状態に更新
          </Button>
          {loading && <InlineSpinner isLoading />}
        </div>

        {error && (
          <CardBase>
            <Note><MdError size={14} style={{ verticalAlign: 'middle', color: '#c94444', marginRight: 4 }} />エラー: {error}</Note>
          </CardBase>
        )}

        {summary && (
          <div style={S.kpiRow}>
            <div style={S.kpiCard('#48859d')}>
              <div style={S.kpiLabel}>対象日数</div>
              <div style={S.kpiValue}>{summary.total_days}</div>
            </div>
            <div style={S.kpiCard('#2da663')}>
              <div style={S.kpiLabel}>登録済</div>
              <div style={S.kpiValue}>{summary.registered_count}</div>
            </div>
            <div style={S.kpiCard('#d68a3c')}>
              <div style={S.kpiLabel}>未処理</div>
              <div style={S.kpiValue}>{summary.pending_count}</div>
            </div>
            <div style={S.kpiCard('#8a6cd6')}>
              <div style={S.kpiLabel}>銀行入金 合計</div>
              <div style={S.kpiValue}>{yen(summary.bank_amount_total)}</div>
            </div>
          </div>
        )}

        {/* Per-day cards */}
        {preview?.days.length === 0 && (
          <CardBase>
            <Note>対象月に ALSOK 入金データがありません。 ARICA 連携 が遅延している可能性があります。</Note>
          </CardBase>
        )}
        {preview?.days.map((day) => (
          <DayCard
            key={day.closing_date}
            day={day}
            onApprove={handleApprove}
            registering={registeringDate === day.closing_date}
          />
        ))}

        {/* =================================================================== */}
        {/* 振替バックフィル (取引B/C/D — 入金機投入高 → wallet 振替)            */}
        {/* =================================================================== */}
        <CardBase>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' as const, marginBottom: '8px' }}>
            <span style={{ fontSize: '1.0625rem', fontWeight: 700 }}>
              <MdSwapHoriz size={20} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              振替バックフィル (取引B/C/D — 入金機投入高 → wallet)
            </span>
            <Button onClick={loadTransferPreview} disabled={transferLoading} small>
              <MdRefresh size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              更新
            </Button>
            {transferLoading && <InlineSpinner isLoading />}
          </div>
          <Note>
            2026-04 以降、 取引A (= alsok:DATE:a) は登録済みだが、 入金機投入高 の本部滞留を
            wallet (本部両替金 6361178 / 本部入金高 6361308) へ相殺する 振替 (取引B/C/D) が抜けている。
            ここで 取引A から相殺対象額を読み取り (recompute なし)、 不足分の 振替のみ登録する。
            <b> 取引A は生成・変更しない (= 二重計上は起きない)。</b>
          </Note>

          {transferError && (
            <div style={{ marginTop: '8px' }}>
              <Note><MdError size={14} style={{ verticalAlign: 'middle', color: '#c94444', marginRight: 4 }} />エラー: {transferError}</Note>
            </div>
          )}

          {transferSummary && (
            <div style={{ ...S.kpiRow, marginTop: '12px' }}>
              <div style={S.kpiCard('#48859d')}>
                <div style={S.kpiLabel}>対象日数</div>
                <div style={S.kpiValue}>{transferSummary.total_days}</div>
              </div>
              <div style={S.kpiCard('#d68a3c')}>
                <div style={S.kpiLabel}>振替未登録</div>
                <div style={S.kpiValue}>{transferSummary.needs_transfer}</div>
              </div>
              <div style={S.kpiCard('#2da663')}>
                <div style={S.kpiLabel}>振替完了</div>
                <div style={S.kpiValue}>{transferSummary.fully_registered}</div>
              </div>
              <div style={S.kpiCard('#c94444')}>
                <div style={S.kpiLabel}>ブロック</div>
                <div style={S.kpiValue}>{transferSummary.blocked}</div>
              </div>
            </div>
          )}

          <div style={{ marginTop: '12px' }}>
            {transferPreview && transferPreview.days.length === 0 && (
              <Note>対象月に 振替 (取引B/C/D) が必要な日はありません。</Note>
            )}
            {transferPreview?.days.map((tDay) => (
              <TransferDayCard
                key={tDay.closing_date}
                day={tDay}
                onApprove={handleTransferApprove}
                registering={transferRegisteringDate === tDay.closing_date}
              />
            ))}
          </div>
        </CardBase>

        {/* Confirm dialog */}
        <MessageDialogConfirm
          isOpen={!!confirmDate}
          title={confirmDate ? `${confirmDate} を freee に登録しますか?` : ''}
          confirmButtonLabel="登録 + 消込実行"
          closeButtonLabel="キャンセル"
          onConfirm={() => confirmDate && performRegister(confirmDate)}
          onRequestClose={() => setConfirmDate(null)}
          contentAlign="left"
        >
          ARICA データから取引A (= 各拠点売上 credit + 本部両替金 debit) を作成し、
          必要なら取引B (= 本部両替金 wallet 入金) も同時作成します。
          その後、 三井住友普通の wallet_txn と reconcile (= 消込) します。
          <br /><br />
          重複防止 4 層 (alsok_runs PK / freee ref_number / invoice_registrations / freee /api/1/deals search) で
          既に登録されている場合は skip します。
        </MessageDialogConfirm>

        {/* Transfer confirm dialog */}
        <MessageDialogConfirm
          isOpen={!!transferConfirmDate}
          title={transferConfirmDate ? `${transferConfirmDate} の振替 (取引B/C/D) を登録しますか?` : ''}
          confirmButtonLabel="承認 → 振替B/C/D 登録"
          closeButtonLabel="キャンセル"
          onConfirm={() => transferConfirmDate && performTransferRegister(transferConfirmDate)}
          onRequestClose={() => setTransferConfirmDate(null)}
          contentAlign="left"
        >
          取引A は生成・変更しません。 入金機投入高の本部滞留分を wallet
          (本部両替金 6361178 / 本部入金高 6361308) へ相殺振替するだけです。 二重計上は起きません。
          <br /><br />
          金額は既存 取引A の実明細から読み取った額をそのまま使用します (= recompute なし)。
          既に登録済みの 取引B/C/D は skip します (= 冪等)。
        </MessageDialogConfirm>

        {/* Result modal */}
        <MessageDialog
          isOpen={!!resultMsg}
          title="登録結果"
          closeButtonLabel="閉じる"
          onRequestClose={() => setResultMsg(null)}
          contentAlign="left"
        >
          {resultMsg || ''}
        </MessageDialog>
      </Container>
    </div>
  );
};
